import type { TaskStatus, TaskPriority } from '../types/models';

/**
 * Maps free-form Jira status strings to our internal enum.
 */
export const normaliseStatus = (raw: string): TaskStatus => {
  const s = raw.trim().toLowerCase();
  if (!s) return 'IN_PROGRESS';
  if (
    s === 'done' ||
    s === 'closed' ||
    s === 'resolved' ||
    s === 'completed' ||
    s === 'complete'
  )
    return 'COMPLETED';
  if (s === 'blocked' || s === 'on hold' || s === 'hold' || s === 'paused')
    return 'BLOCKED';
  if (
    s.includes('test') ||
    s.includes('discussion') ||
    s === 'review' ||
    s === 'in review' ||
    s === 'qa'
  )
    return 'DISCUSSION';
  return 'IN_PROGRESS';
};

export const normalisePriority = (raw: string): TaskPriority => {
  const s = raw.trim().toLowerCase();
  if (s === 'critical' || s === 'highest' || s === 'blocker') return 'CRITICAL';
  if (s === 'high' || s === 'major') return 'HIGH';
  if (s === 'low' || s === 'minor' || s === 'trivial') return 'LOW';
  return 'MEDIUM';
};

export const STANDARD_TARGETS = [
  { key: 'ticketNo', label: 'Ticket No.', required: true, examples: ['Issue Key', 'Key'] },
  { key: 'title', label: 'Title', required: true, examples: ['Summary', 'Title'] },
  { key: 'description', label: 'Description', required: false, examples: ['Description'] },
  { key: 'assignee', label: 'Assignee', required: false, examples: ['Assignee'] },
  { key: 'status', label: 'Status', required: false, examples: ['Status'] },
  { key: 'priority', label: 'Priority', required: false, examples: ['Priority'] },
  { key: 'storyPoints', label: 'Story Points', required: false, examples: ['Story Points', 'Custom field (Story Points)'] },
] as const;

export type StandardField = typeof STANDARD_TARGETS[number]['key'];

/**
 * Best-effort auto-detection of source columns based on common Jira exports.
 */
export const autoDetectMapping = (
  columns: string[],
): Record<StandardField, string | null> => {
  const m: Record<StandardField, string | null> = {
    ticketNo: null,
    title: null,
    description: null,
    assignee: null,
    status: null,
    priority: null,
    storyPoints: null,
  };

  const lower = columns.map((c) => c.toLowerCase());

  const find = (...patterns: string[]) => {
    for (const p of patterns) {
      const idx = lower.findIndex((c) => c === p || c.includes(p));
      if (idx >= 0) return columns[idx];
    }
    return null;
  };

  m.ticketNo = find('issue key', 'key');
  m.title = find('summary', 'title');
  m.description = find('description');
  m.assignee = find('assignee');
  m.status = find('status');
  m.priority = find('priority');
  m.storyPoints = find('story points', 'storypoints', 'story point');

  return m;
};
